/* Jireh Threads form validation + spam protection (contact page, blog comments).
   - Validates on blur and on submit; shows a message under each field and a summary at the top.
   - Trims and normalises values before sending; blocks control characters and links-only spam.
   - Honeypot field, minimum fill time, and a one-minute resend cooldown (kept in this browser only).
   - Only ever uses textContent for messages. */
(function () {
  'use strict';

  var PATTERNS = {
    name: /^[A-Za-zÀ-ÖØ-öø-ÿ'’.\- ]{2,80}$/,
    phone: /^[0-9+()\-. ]{7,20}$/,
    order: /^#?[0-9]{3,10}$/,
    email: /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[A-Za-z]{2,}$/
  };
  var CONTROL = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/;
  var MIN_FILL_MS = 3000;
  var COOLDOWN_MS = 60000;

  function store(key, val) {
    try {
      if (val === undefined) return parseInt(localStorage.getItem(key), 10) || 0;
      localStorage.setItem(key, String(val));
    } catch (e) { return 0; }
  }

  function countLinks(text) { return (text.match(/https?:\/\/|www\./gi) || []).length; }

  function validateField(el) {
    var v = el.type === 'checkbox' ? el.checked : (el.value || '');
    if (typeof v === 'string') v = v.replace(/\s+/g, ' ').trim();
    if (el.tagName === 'TEXTAREA') v = (el.value || '').trim();
    var msg = '';
    var wrap = el.closest('[data-field]');
    if (wrap && wrap.hidden) return '';

    if (el.required && (v === '' || v === false)) msg = el.dataset.msgRequired || 'This field is required.';
    else if (typeof v === 'string' && v !== '') {
      if (CONTROL.test(v)) msg = 'Please remove unusual characters.';
      else if (el.minLength > 0 && v.length < el.minLength) msg = el.dataset.msgShort || ('Please enter at least ' + el.minLength + ' characters.');
      else if (el.maxLength > 0 && v.length > el.maxLength) msg = 'Please keep this under ' + el.maxLength + ' characters.';
      else if (el.type === 'email' && !PATTERNS.email.test(v)) msg = el.dataset.msgType || 'Enter a valid email address.';
      else if (el.dataset.pattern && PATTERNS[el.dataset.pattern] && !PATTERNS[el.dataset.pattern].test(v)) msg = el.dataset.msgPattern || 'Please check this field.';
      else if (el.tagName === 'TEXTAREA' && countLinks(v) > 2) msg = 'Please include no more than 2 links.';
      else if (el.tagName === 'TEXTAREA' && /<\s*\/?\s*(script|iframe|a|img)\b/i.test(v)) msg = 'Please remove HTML from your message.';
    }
    showError(el, msg);
    return msg;
  }

  function showError(el, msg) {
    var id = (el.getAttribute('aria-describedby') || '').split(' ')[0];
    var box = id ? document.getElementById(id) : null;
    var wrap = el.closest('[data-field]');
    if (msg) {
      el.setAttribute('aria-invalid', 'true');
      if (box) { box.textContent = msg; box.hidden = false; }
      if (wrap) wrap.classList.add('is-error');
    } else {
      el.removeAttribute('aria-invalid');
      if (box) { box.textContent = ''; box.hidden = true; }
      if (wrap) { wrap.classList.remove('is-error'); if (el.value && el.type !== 'checkbox') wrap.classList.add('is-ok'); else wrap.classList.remove('is-ok'); }
    }
  }

  function init(form) {
    if (form.dataset.ready) return;
    form.dataset.ready = '1';
    var started = Date.now();
    var fields = Array.prototype.slice.call(form.querySelectorAll('input:not([type=hidden]):not([name=jt_website]), select, textarea'));
    var summary = form.querySelector('[data-summary]');
    var list = form.querySelector('[data-summary-list]');
    var wait = form.querySelector('[data-wait]');
    var submit = form.querySelector('[data-submit]');
    var honeypot = form.querySelector('[name=jt_website]');
    var topic = form.querySelector('[data-topic]');
    var orderField = form.querySelector('[data-order-field]');
    var counter = form.querySelector('[data-count]');
    var msg = form.querySelector('textarea');
    var coolKey = 'jt-form-sent-' + (form.id || 'form');

    // Server-side errors: focus the summary so screen readers announce it
    if (summary && !summary.hidden) summary.focus();
    var done = form.querySelector('[data-done]');
    if (done) { done.focus(); store(coolKey, Date.now()); return; }

    fields.forEach(function (el) {
      el.addEventListener('blur', function () { if (el.value || el.getAttribute('aria-invalid')) validateField(el); });
      el.addEventListener('input', function () { if (el.getAttribute('aria-invalid')) validateField(el); });
      el.addEventListener('change', function () { if (el.type === 'checkbox' || el.tagName === 'SELECT') validateField(el); });
    });

    // Show the order number field only for order-related topics
    function syncTopic() {
      if (!topic || !orderField) return;
      var t = (topic.value || '').toLowerCase();
      var show = /order|return|exchange|deliver|shipping/.test(t);
      orderField.hidden = !show;
      if (!show) { var i = orderField.querySelector('input'); i.value = ''; showError(i, ''); }
    }
    if (topic) { topic.addEventListener('change', syncTopic); syncTopic(); }

    // Live character counter
    function syncCount() {
      if (!counter || !msg) return;
      var n = msg.value.length, max = msg.maxLength > 0 ? msg.maxLength : 2000;
      counter.textContent = n + ' / ' + max;
      counter.classList.toggle('is-near', n > max * 0.9);
    }
    if (msg) { msg.addEventListener('input', syncCount); syncCount(); }

    form.addEventListener('submit', function (e) {
      // Bot checks: honeypot filled or form filled impossibly fast -> silently stop
      if (honeypot && honeypot.value) { e.preventDefault(); return; }
      if (Date.now() - started < MIN_FILL_MS) {
        e.preventDefault();
        if (wait) { wait.textContent = 'Please take a moment to check your message, then send it again.'; wait.hidden = false; }
        return;
      }
      // Cooldown after a recent send
      var last = store(coolKey);
      if (last && Date.now() - last < COOLDOWN_MS) {
        e.preventDefault();
        var secs = Math.ceil((COOLDOWN_MS - (Date.now() - last)) / 1000);
        if (wait) { wait.textContent = 'You just sent a message. You can send another in ' + secs + ' seconds.'; wait.hidden = false; }
        return;
      }
      if (wait) wait.hidden = true;

      // Normalise values before sending
      fields.forEach(function (el) {
        if (el.type === 'checkbox' || el.tagName === 'SELECT') return;
        if (el.tagName === 'TEXTAREA') el.value = el.value.replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
        else el.value = el.value.replace(/\s+/g, ' ').trim();
        if (el.type === 'email') el.value = el.value.toLowerCase();
      });

      var errors = [];
      fields.forEach(function (el) {
        var m = validateField(el);
        if (m) {
          var label = form.querySelector('label[for="' + el.id + '"]');
          errors.push({ el: el, text: (label ? label.textContent.replace('*', '').replace(/\(.*\)/, '').trim() + ': ' : '') + m });
        }
      });

      if (errors.length) {
        e.preventDefault();
        if (summary && list) {
          list.textContent = '';
          errors.forEach(function (er) {
            var li = document.createElement('li');
            var a = document.createElement('a');
            a.href = '#' + er.el.id;
            a.textContent = er.text;
            a.addEventListener('click', function (ev) { ev.preventDefault(); er.el.focus(); });
            li.appendChild(a); list.appendChild(li);
          });
          summary.hidden = false;
          summary.focus();
        } else {
          errors[0].el.focus();
        }
        return;
      }

      // Looks good: lock the button to prevent double sends
      if (submit) {
        submit.disabled = true;
        submit.classList.add('is-loading');
        var l = submit.querySelector('[data-submit-label]');
        if (l) l.textContent = 'Sending…';
      }
      // The cooldown starts only once Shopify confirms the message was sent (see the success state above).
    });
  }

  function boot() { document.querySelectorAll('form[data-jt-contact], form[data-jt-validate]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
